import { useEffect, useRef, useState } from "react";

interface VideoPlayerDemoProps {
  poster: string;
  title: string;
  isLive: boolean;
}

export default function VideoPlayerDemo({ poster, title, isLive }: VideoPlayerDemoProps) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(18);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (playing) {
      timerRef.current = window.setInterval(() => {
        setProgress((p) => (p >= 100 ? 0 : p + 0.5));
      }, 600);
    } else if (timerRef.current) {
      window.clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [playing]);

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-950">
      <div className="relative aspect-video w-full">
        <img src={poster} alt={title} title={`${title} - NBA直播`} className="h-full w-full object-top opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-background-950/35 to-background-950/50" />

        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
          <i className="ri-error-warning-line" /> 前端演示画面
        </span>
        {isLive && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-background-950/70 px-2.5 py-1 text-xs font-semibold text-background-50">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-live-pulse" /> LIVE
          </span>
        )}

        <button
          type="button"
          aria-label={playing ? "暂停" : "播放"}
          onClick={() => setPlaying((v) => !v)}
          className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform hover:scale-105"
        >
          <i className={playing ? "ri-pause-fill text-3xl" : "ri-play-fill text-3xl"} />
        </button>

        <div className="absolute inset-x-0 bottom-0 px-3 pb-3 pt-6">
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              aria-label={playing ? "暂停" : "播放"}
              onClick={() => setPlaying((v) => !v)}
              className="flex w-8 h-8 items-center justify-center rounded text-background-50"
            >
              <i className={playing ? "ri-pause-line" : "ri-play-line"} />
            </button>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-50/30">
              <div className="h-full rounded-full bg-accent-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-xs text-background-100 tabular-nums">
              {isLive ? "直播中" : `${Math.floor(progress)}%`}
            </span>
            <button
              type="button"
              aria-label={muted ? "取消静音" : "静音"}
              onClick={() => setMuted((v) => !v)}
              className="flex w-8 h-8 items-center justify-center rounded text-background-50"
            >
              <i className={muted ? "ri-volume-mute-line" : "ri-volume-up-line"} />
            </button>
          </div>
          <p className="text-xs text-background-200">
            <i className="ri-information-line" /> 该播放器为界面演示，不包含真实视频流。
          </p>
        </div>
      </div>
    </div>
  );
}